import React, { useRef, useEffect, useState } from 'react'
import * as THREE from 'three'

// 🚀 DEBUG FLAG - ustaw na true tylko do debugowania
const DEBUG_LOGS = false
import {
  SceneRefs,
  FenceConfig,
  Position3D,
  initializeScene,
  resetCamera,
  zoomToFit,
  handleResize,
  disposeScene,
  loadDAEModel,
  loadHouseModel,
  ElementManager,
  handleMouseClick,
  handleMouseMove,
  handleScroll,
  handleKeyDown,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  animate,
  RotationManager,
  PerimeterCloser,
} from '../utils/threejs'
import { getFenceConfigWithColor } from '../config/fenceConfigs'

// 🎯 MAPA OFFSETÓW DLA WSZYSTKICH KONFIGURACJI
const ROTATION_RIGHT_EDGE_OFFSETS: Record<string, Record<string, number>> = {
  // 🚪 Offsety dla gate/door (są takie same dla wszystkich konfiguracji)
  'gate': {
    '1': -0.108,  // Jak door 1x - kompensuj różnicę rightOffset
    '2': -0.198,  // Jak door 2x
    '3': -0.396,  // Jak door 3x
    '4': -0.594,  // Jak door 4x
    '5': -0.792   // Jak door 5x
  },
  'door': {
    '1': -0.108,  // Kompensuj różnicę rightOffset (0.306 - 0.198 = 0.108)
    '2': -0.198,  // Drzwi 2x - jak section
    '3': -0.396,  // Drzwi 3x - jak section
    '4': -0.594,  // Drzwi 4x - jak section
    '5': -0.792   // Drzwi 5x - jak section
  },
  'vision-bet-long': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'slim-3d': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'gladkie_long': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'royal_s50': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'gladkie_max': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'vision-bet-max': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'slim': {
    '1': 0.0,
    '2': -0.119,
    '3': -0.238,
    '4': -0.356,
    '5': -0.474
  },
  'royal': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.198,
    '4': 0.0,
    '5': 0.0
  },
  'royal_p50_5': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.198,
    '4': 0.0,
    '5': 0.0
  },
  'lupane-muro': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.0,
    '4': 0.0,
    '5': 0.0
  },
  'gladkie_medium': {
    '1': 0,
    '2': -0.158,
    '3': -0.316,
    '4': -0.474,
    '5': -0.632
  },
  'vision-bet-medium': {
    '1': 0,
    '2': -0.158,
    '3': -0.316,
    '4': -0.474,
    '5': -0.632
  },
  'royal_p50': {
    '1': 0.0,
    '2': -0.198,
    '3': -0.396,
    '4': -0.594,
    '5': -0.792
  },
  'lupane-muro-p22': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.0,
    '4': 0.0,
    '5': 0.0
  },
  'lupane-muro-p38': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.0,
    '4': 0.0,
    '5': 0.0
  },
  'lupane-muro-s22': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.0,
    '4': 0.0,
    '5': 0.0
  },
  'lupane-muro-s38': {
    '1': 0.0,
    '2': 0.0,
    '3': 0.0,
    '4': 0.0,
    '5': 0.0
  }
}

/**
 * Interfejsy
 */
interface FenceElement {
  id: string
  type: 'post' | 'gate' | 'section' | 'door'
  position: { x: number; z: number }
  width?: number
  height?: number
}

interface FenceSystem {
  id: string
  name: string
  objFile: string
  mtlFile: string
  textureFile: string
  basePrice: number
}

interface HouseStyle {
  id: string
  name: string
  model: string
}

interface ThreeJSCanvas3DProps {
  elements: FenceElement[]
  selectedElementId: string | null
  onElementSelect: (id: string) => void
  fenceSystem: FenceSystem | null
  houseStyle: HouseStyle | null
  onReset: () => void
  selectedFence?: any
  viewMode: '2D' | '3D'
  resetCounter: number
  selectedColor?: string | null
}

/**
 * Komponent Three.js Canvas 3D - zrefaktoryzowany
 */
const ThreeJSCanvas3D: React.FC<ThreeJSCanvas3DProps> = ({
  elements: _elements,
  selectedElementId: _selectedElementId,
  onElementSelect,
  fenceSystem: _fenceSystem,
  houseStyle,
  onReset: _onReset,
  selectedFence,
  viewMode,
  resetCounter,
  selectedColor,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRefsRef = useRef<SceneRefs>({
    scene: null,
    renderer: null,
    camera: null,
    controls: null,
    raycaster: null,
    mouse: null,
  })

  const [elementManager] = useState(() => new ElementManager())
  const [rotationManager] = useState(() => new RotationManager())
  const [preparedModel, setPreparedModel] = useState<THREE.Scene | null>(null)
  const [houseModel, setHouseModel] = useState<THREE.Scene | null>(null)
  const houseModelRef = useRef<THREE.Scene | null>(null)

  // Normalizuj selectedColor
  const normalizedSelectedColor =
    selectedColor && typeof selectedColor === 'object'
      ? ((selectedColor as any).name || (selectedColor as any).id || null)
      : selectedColor

  /**
   * Inicjalizacja sceny
   */
  useEffect(() => {
    if (!containerRef.current) return
    
    let refs: SceneRefs | null = null
    
    try {
      refs = initializeScene(containerRef.current)
      sceneRefsRef.current = refs
      
      console.log('✅ ThreeJSCanvas3D: Scena zainicjalizowana:', refs.scene)
      console.log('✅ ThreeJSCanvas3D: Renderer:', refs.renderer)
      console.log('✅ ThreeJSCanvas3D: Camera:', refs.camera)
    } catch (error) {
      console.error('❌ ThreeJSCanvas3D: Błąd przy inicjalizacji sceny:', error)
      // Wyświetl komunikat błędu użytkownikowi
      if (containerRef.current) {
        containerRef.current.innerHTML = `
          <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 20px; text-align: center; color: #ef4444;">
            <h2 style="margin-bottom: 10px;">Błąd inicjalizacji WebGL</h2>
            <p style="margin-bottom: 5px;">${error instanceof Error ? error.message : 'Nie można utworzyć kontekstu WebGL.'}</p>
            <p style="font-size: 14px; color: #6b7280; margin-top: 10px;">
              Upewnij się, że Twoja przeglądarka wspiera WebGL i że jest włączony.
            </p>
          </div>
        `
      }
      return
    }
    
    if (!refs) return
    
    // Event listeners
    let handleCanvasClick: ((e: MouseEvent) => void) | null = null
    let handleCanvasMouseMove: (() => void) | null = null
    let handleCanvasTouchStart: ((e: TouchEvent) => void) | null = null
    let handleCanvasTouchMove: ((e: TouchEvent) => void) | null = null
    let handleCanvasTouchEnd: ((e: TouchEvent) => void) | null = null
    let handleResizeWrapper: (() => void) | null = null
    let handleScrollWrapper: (() => void) | null = null
    let handleKeyDownWrapper: (() => void) | null = null

    if (refs.renderer) {
      const canvas = refs.renderer.domElement

      handleCanvasClick = (e: MouseEvent) => {
        // 🎯 Jeśli użytkownik wybiera punkt startu – użyj kliknięcia do wyznaczenia pozycji na płaszczyźnie Y=0
        // ⚠️ BLOKADA: Nie pozwalaj wybrać punktu startu, jeśli są już elementy w projekcie
        if ((window as any).__isSelectingStartPoint) {
          // Sprawdź, czy są już elementy w scenie 3D
          const existingElements = elementManager.getAllElements()
          if (existingElements.size > 0) {
            console.log('⚠️ Nie można wybrać punktu startu - są już elementy w projekcie')
            ;(window as any).__isSelectingStartPoint = false
            // Przywróć domyślny kursor
            const canvasContainer = document.querySelector('.threejs-canvas') as HTMLDivElement | null
            if (canvasContainer) {
              const canvasEl = canvasContainer.querySelector('canvas') as HTMLCanvasElement | null
              if (canvasEl) {
                canvasEl.style.cursor = 'default'
              }
            }
            return
          }
          
          if (!sceneRefsRef.current.raycaster || !sceneRefsRef.current.mouse || !sceneRefsRef.current.camera || !containerRef.current) {
            return
          }

          const rect = containerRef.current.getBoundingClientRect()
          sceneRefsRef.current.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
          sceneRefsRef.current.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1

          sceneRefsRef.current.raycaster.setFromCamera(
            sceneRefsRef.current.mouse,
            sceneRefsRef.current.camera
          )

          // Płaszczyzna na poziomie Y=0
          const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
          const intersection = new THREE.Vector3()

          if (sceneRefsRef.current.raycaster.ray.intersectPlane(plane, intersection)) {
            const startPoint = { x: intersection.x, y: 0, z: intersection.z }
            ;(window as any).__fenceStartPoint = startPoint
            ;(window as any).__isSelectingStartPoint = false

            // Przywróć domyślny kursor po wybraniu punktu startu
            const canvasContainer = document.querySelector('.threejs-canvas') as HTMLDivElement | null
            if (canvasContainer) {
              const canvasEl = canvasContainer.querySelector('canvas') as HTMLCanvasElement | null
              if (canvasEl) {
                canvasEl.style.cursor = 'default'
              }
            }

            // 🎯 Powiadom React, że wybrano punkt startu
            if (typeof window !== 'undefined' && typeof (window as any).dispatchEvent === 'function') {
              window.dispatchEvent(new CustomEvent('fenceStartPointSelected'))
            }
          }

          // Nie próbuj wybierać istniejącego elementu przy tym kliknięciu
          return
        }

        handleMouseClick(
          e,
          containerRef.current!,
          refs,
          elementManager.getAllElements(),
          onElementSelect
        )
      }

      handleCanvasMouseMove = () => handleMouseMove(refs)
      handleCanvasTouchStart = (e: TouchEvent) =>
        handleTouchStart(
          e,
          containerRef.current!,
          refs,
          elementManager.getAllElements(),
          onElementSelect
        )
      handleCanvasTouchMove = (e: TouchEvent) => handleTouchMove(e, refs)
      handleCanvasTouchEnd = (e: TouchEvent) => handleTouchEnd(e, refs)

      canvas.addEventListener('click', handleCanvasClick)
      canvas.addEventListener('mousemove', handleCanvasMouseMove)
      canvas.addEventListener('touchstart', handleCanvasTouchStart)
      canvas.addEventListener('touchmove', handleCanvasTouchMove)
      canvas.addEventListener('touchend', handleCanvasTouchEnd)
    }

    handleResizeWrapper = () => {
      if (containerRef.current) {
        handleResize(containerRef.current, refs)
      }
    }
    handleScrollWrapper = () => handleScroll(refs)
    handleKeyDownWrapper = () => handleKeyDown(refs)

    window.addEventListener('resize', handleResizeWrapper)
    window.addEventListener('scroll', handleScrollWrapper)
    window.addEventListener('keydown', handleKeyDownWrapper)

    // Animacja
    animate(refs)

    // Cleanup
    return () => {
      if (refs.renderer && containerRef.current) {
        const canvas = refs.renderer.domElement
        if (handleCanvasClick) canvas.removeEventListener('click', handleCanvasClick)
        if (handleCanvasMouseMove) canvas.removeEventListener('mousemove', handleCanvasMouseMove)
        if (handleCanvasTouchStart) canvas.removeEventListener('touchstart', handleCanvasTouchStart)
        if (handleCanvasTouchMove) canvas.removeEventListener('touchmove', handleCanvasTouchMove)
        if (handleCanvasTouchEnd) canvas.removeEventListener('touchend', handleCanvasTouchEnd)
        containerRef.current.removeChild(canvas)
      }

      if (handleResizeWrapper) window.removeEventListener('resize', handleResizeWrapper)
      if (handleScrollWrapper) window.removeEventListener('scroll', handleScrollWrapper)
      if (handleKeyDownWrapper) window.removeEventListener('keydown', handleKeyDownWrapper)

      disposeScene(refs)
    }
  }, [])

  /**
   * Ładowanie modelu DAE gdy zmienia się selectedFence
   */
  useEffect(() => {
    const loadModel = async () => {
      if (selectedFence?.daeFile) {
        // Reset sceny
        if (sceneRefsRef.current.scene) {
          elementManager.clearAll(sceneRefsRef.current.scene)
        }

        // Załaduj model
        const fenceConfig: FenceConfig = {
          ...selectedFence,
          id: selectedFence.id || 'gladkie_medium',
        }

        const model = await loadDAEModel(fenceConfig, normalizedSelectedColor || undefined)
        setPreparedModel(model)
        // 🚀 Cache będzie automatycznie przygotowany przy pierwszym użyciu przez cloneElementWithCache
      }
    }

    loadModel().catch(console.error)
  }, [selectedFence, normalizedSelectedColor, elementManager])

  /**
   * Ładowanie modelu domu
   */
  useEffect(() => {
    console.log('🏠 useEffect houseStyle:', houseStyle, 'scene:', sceneRefsRef.current.scene ? 'TAK' : 'NIE')
    
    // Poczekaj na scenę
    if (!sceneRefsRef.current.scene) {
      console.log('🏠 Brak sceny - pomijam')
      return
    }
    
    // Zawsze najpierw usuń poprzedni model domu (jeśli istnieje w ref)
    if (houseModelRef.current && sceneRefsRef.current.scene) {
      console.log('🏠 Usuwam poprzedni model domu')
      sceneRefsRef.current.scene.remove(houseModelRef.current)
      houseModelRef.current = null
      setHouseModel(null)
    }
    
    const loadHouse = async () => {
      if (houseStyle && houseStyle.model) {
        console.log('🏠 Ładuję dom:', houseStyle.id, houseStyle.model)
        
        // Załaduj nowy model z houseStyle.model
        try {
          const model = await loadHouseModel(houseStyle.model)
          console.log('🏠 Model załadowany:', model ? 'TAK' : 'NIE')
          if (model && sceneRefsRef.current.scene) {
            sceneRefsRef.current.scene.add(model)
            houseModelRef.current = model
            setHouseModel(model)
            console.log('🏠 Model dodany do sceny')
          }
        } catch (err) {
          console.error('🏠 Błąd loadHouseModel:', err)
        }
      }
    }

    loadHouse()
    
    // Cleanup: usuń model przy zmianie houseStyle lub unmount
    return () => {
      if (houseModelRef.current && sceneRefsRef.current.scene) {
        console.log('🏠 Cleanup: usuwam model domu')
        sceneRefsRef.current.scene.remove(houseModelRef.current)
        houseModelRef.current = null
      }
    }
  }, [houseStyle])

  /**
   * Eksport funkcji podstawowych do window (dostępne od razu)
   */
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Funkcja getThreeJSScene (dla Canvas2D)
    ;(window as any).getThreeJSScene = () => {
      return sceneRefsRef.current.scene
    }

    // Funkcja getThreeJSRenderer (dla Canvas2D)
    ;(window as any).getThreeJSRenderer = () => {
      return sceneRefsRef.current.renderer
    }

    // Funkcja getThreeJSContainer (dla Canvas2D)
    ;(window as any).getThreeJSContainer = () => {
      return containerRef.current
    }

    // Funkcja removeFenceElement
    ;(window as any).removeFenceElement = (elementId: string) => {
      if (sceneRefsRef.current.scene) {
        elementManager.removeFenceElement(elementId, sceneRefsRef.current.scene)
      }
    }

    // Funkcja clearAllFenceElements
    ;(window as any).clearAllFenceElements = () => {
      if (sceneRefsRef.current.scene) {
        elementManager.clearAll(sceneRefsRef.current.scene)
      }
    }

    // Funkcja resetElementRotation
    ;(window as any).resetElementRotation = (elementId: string) => {
      const element = elementManager.getElement(elementId)
      if (element) {
        rotationManager.resetElementRotation(element)
      }
    }

    // Funkcja updateElementPosition
    ;(window as any).updateElementPosition = (elementId: string, position: { x: number, y: number, z: number }) => {
      elementManager.updateElementPosition(elementId, position)
    }

    // Funkcja getAllFenceElements
    ;(window as any).getAllFenceElements = () => {
      const elements = Array.from(elementManager.getAllElements().entries()).map(([id, element]) => ({
        id,
        type: element.userData.elementType,
        position: element.position,
        rotation: element.rotation.y * 180 / Math.PI
      }))
      return elements
    }

    // Funkcja syncReactStateWith3D
    ;(window as any).syncReactStateWith3D = () => {
      const elements3D = Array.from(elementManager.getAllElements().entries()).map(([id, element]) => ({
        id,
        type: element.userData.elementType,
        position: element.position,
        rotation: element.rotation.y * 180 / Math.PI,
        width: element.userData.width || 2,
        height: element.userData.height || 2
      }))
      return elements3D
    }

    // Referencja do elements3DRef (kompatybilność)
    ;(window as any).elements3DRef = { current: elementManager.getAllElements() }

    // Funkcja updatePostHeight - dostępna od razu, sprawdza czy model jest gotowy
    ;(window as any).updatePostHeight = async (elementId: string, newHeight: number) => {
      if (!preparedModel) {
        console.warn('⚠️ updatePostHeight: Model nie jest jeszcze załadowany, spróbuj ponownie za chwilę')
        return
      }
      
      // 🎯 Dla Royal Mix / Muro Mix: Załaduj konfigurację wariantu z userData elementu
      let fenceConfig: FenceConfig = {
        ...selectedFence,
        id: selectedFence?.id || 'gladkie_medium',
      }
      
      const element = elementManager.getAllElements().get(elementId)
      if (element) {
        const variant = element.userData?.variant
        const originalFenceConfigId = element.userData?.originalFenceConfigId
        
        // 🎯 Dla Royal Mix: Załaduj konfigurację wariantu
        if (variant && originalFenceConfigId === 'royal-mix' && (variant === 's50' || variant === 'p50')) {
          DEBUG_LOGS && console.log(`🎯 Royal Mix updatePostHeight: Element ma variant=${variant}, ładuję konfigurację royal_${variant}`)
          const variantConfigId = variant === 's50' ? 'royal_s50' : 'royal_p50'
          const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
          const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor || undefined)
          if (variantConfig) {
            fenceConfig = variantConfig
            DEBUG_LOGS && console.log(`✅ Załadowano konfigurację wariantu: ${fenceConfig.id}`)
          }
        }
        // 🎯 Dla Muro Mix: Załaduj konfigurację wariantu
        else if (variant && originalFenceConfigId === 'lupane-muro-mix' && (variant === 'p22' || variant === 'p38' || variant === 's22' || variant === 's38')) {
          DEBUG_LOGS && console.log(`🎯 Muro Mix updatePostHeight: Element ma variant=${variant}, ładuję konfigurację lupane-muro-${variant}`)
          const variantConfigId = `lupane-muro-${variant}`
          const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
          const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor || undefined)
          if (variantConfig) {
            fenceConfig = variantConfig
            DEBUG_LOGS && console.log(`✅ Załadowano konfigurację wariantu: ${fenceConfig.id}`)
          }
        }
      }
      
      await elementManager.updatePostHeight(elementId, newHeight, fenceConfig, preparedModel, normalizedSelectedColor || undefined)
    }
    
    // Funkcja updateSectionHeight - dostępna od razu, sprawdza czy model jest gotowy
    ;(window as any).updateSectionHeight = async (elementId: string, newHeight: number) => {
      if (!preparedModel) {
        console.warn('⚠️ updateSectionHeight: Model nie jest jeszcze załadowany, spróbuj ponownie za chwilę')
        return
      }
      
      // 🎯 Dla Royal Mix / Muro Mix: Załaduj konfigurację wariantu z userData elementu
      let fenceConfig: FenceConfig = {
        ...selectedFence,
        id: selectedFence?.id || 'gladkie_medium',
      }
      
      const element = elementManager.getAllElements().get(elementId)
      if (element) {
        const variant = element.userData?.variant
        const originalFenceConfigId = element.userData?.originalFenceConfigId
        
        // 🎯 Dla Royal Mix: Załaduj konfigurację wariantu
        if (variant && originalFenceConfigId === 'royal-mix' && (variant === 's50' || variant === 'p50')) {
          DEBUG_LOGS && console.log(`🎯 Royal Mix updateSectionHeight: Element ma variant=${variant}, ładuję konfigurację royal_${variant}`)
          const variantConfigId = variant === 's50' ? 'royal_s50' : 'royal_p50'
          const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
          const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor || undefined)
          if (variantConfig) {
            fenceConfig = variantConfig
            DEBUG_LOGS && console.log(`✅ Załadowano konfigurację wariantu: ${fenceConfig.id}`)
          }
        }
        // 🎯 Dla Muro Mix: Załaduj konfigurację wariantu
        else if (variant && originalFenceConfigId === 'lupane-muro-mix' && (variant === 'p22' || variant === 'p38' || variant === 's22' || variant === 's38')) {
          DEBUG_LOGS && console.log(`🎯 Muro Mix updateSectionHeight: Element ma variant=${variant}, ładuję konfigurację lupane-muro-${variant}`)
          const variantConfigId = `lupane-muro-${variant}`
          const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
          const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor || undefined)
          if (variantConfig) {
            fenceConfig = variantConfig
            DEBUG_LOGS && console.log(`✅ Załadowano konfigurację wariantu: ${fenceConfig.id}`)
          }
        }
      }
      
      await elementManager.updatePostHeight(elementId, newHeight, fenceConfig, preparedModel, normalizedSelectedColor || undefined)
    }
  }, [elementManager, rotationManager, preparedModel, selectedFence])

  /**
   * Eksport funkcji wymagających preparedModel do window
   */
  useEffect(() => {
    if (typeof window === 'undefined' || !preparedModel) return

    // Funkcja addFenceElement
    ;(window as any).addFenceElement = async (
      elementId: string,
      elementType: 'post' | 'section' | 'gate' | 'door',
      position: Position3D,
      fenceData: any,
      _postHeight?: number,
      selectedColorParam?: string | null,
      onElementAdded?: (
        elementId: string,
        elementType: string,
        position: Position3D,
        dimensions: { width: number; height: number }
      ) => void,
      widthMultiplier?: number, // 🎯 Mnożnik szerokości dla przęsła
      variant?: string // 🎯 Wariant elementu (np. 'p22', 'p38', 's22', 's38' dla Łupane Muro - Pełne)
    ) => {
      DEBUG_LOGS && console.log(`🔍 DEBUG: addFenceElement wywołana dla ${elementId} typu ${elementType}`)
      DEBUG_LOGS && console.log(`🔍 DEBUG: fenceData:`, fenceData)
      DEBUG_LOGS && console.log(`🔍 DEBUG: selectedColorParam:`, selectedColorParam)
      DEBUG_LOGS && console.log(`🔍 DEBUG: variant:`, variant)
      
      if (!sceneRefsRef.current.scene) {
        console.error('❌ addFenceElement: Brak sceny')
        return null
      }

      if (!preparedModel) {
        console.error('❌ addFenceElement: Brak preparedModel')
        return null
      }

      console.log(`✅ addFenceElement: Scena i preparedModel dostępne, dodaję element ${elementId}`)

      const result = await elementManager.addFenceElement(
        elementId,
        elementType,
        position,
        sceneRefsRef.current.scene,
        fenceData,
        preparedModel,
        selectedColorParam || normalizedSelectedColor || undefined,
        widthMultiplier || 1, // 🎯 Przekaż mnożnik szerokości (domyślnie 1)
        _postHeight, // 🎯 Przekaż wysokość słupka w blokach (z suwaka w ElementToolbar)
        variant // 🎯 Przekaż wariant elementu
      )

      DEBUG_LOGS && console.log(`🎯 addFenceElement result for ${elementId}:`, result)

      if (result && onElementAdded) {
        DEBUG_LOGS && console.log(`📤 Calling onElementAdded callback with dimensions:`, result.dimensions)
        onElementAdded(elementId, elementType, position, result.dimensions)
      }

      return result // 🎯 Zwróć result, żeby perimeterCloser mógł sprawdzić czy element został dodany
    }

    // Funkcja updateElementRotation
    ;(window as any).updateElementRotation = (elementId: string, newRotation: number) => {
      DEBUG_LOGS && console.log(`🔄 ThreeJSCanvas3D.updateElementRotation: START - elementId: ${elementId}, newRotation: ${newRotation}°`)
      const element = elementManager.getElement(elementId)
      if (element) {
        DEBUG_LOGS && console.log(`✅ ThreeJSCanvas3D.updateElementRotation: Element znaleziony, wywołuję rotationManager.updateElementRotation`)
        rotationManager.updateElementRotation(
          element,
          newRotation,
          elementManager.getAllElements()
        )
        DEBUG_LOGS && console.log(`✅ ThreeJSCanvas3D.updateElementRotation: rotationManager.updateElementRotation zakończona`)
      } else {
        console.warn(`⚠️ ThreeJSCanvas3D.updateElementRotation: Element ${elementId} nie znaleziony!`)
      }
    }

    // Funkcja updateSectionWidth
    ;(window as any).updateSectionWidth = async (elementId: string, newWidthMultiplier: number) => {
      DEBUG_LOGS && console.log(`🎯 3D updateSectionWidth: ${elementId} → ${newWidthMultiplier}x`)
      
      const element = elementManager.getElement(elementId)
      if (!element) {
        console.error(`❌ 3D updateSectionWidth: Element ${elementId} nie znaleziony`)
        return
      }

      // Zaktualizuj widthMultiplier w userData
      element.userData.widthMultiplier = newWidthMultiplier
      
      // Zaktualizuj szerokość wizualną elementu
      const fenceConfig: FenceConfig = {
        ...selectedFence,
        id: selectedFence?.id || 'gladkie_medium',
      }
      
      await elementManager.updateSectionWidth(elementId, newWidthMultiplier, fenceConfig)
      DEBUG_LOGS && console.log(`✅ 3D updateSectionWidth: Zaktualizowano element ${elementId} na ${newWidthMultiplier}x`)
    }

    // Funkcja updateElementId
    ;(window as any).updateElementId = (tempId: string, realId: string) => {
      const element = elementManager.getElement(tempId)
      if (element) {
        // Zaktualizuj ID w userData
        element.userData.fenceElementId = realId
        element.traverse((child: THREE.Object3D) => {
          if (child.userData && child.userData.fenceElementId === tempId) {
            child.userData.fenceElementId = realId
          }
        })

        // Zaktualizuj mapę
        const allElements = elementManager.getAllElements()
        allElements.delete(tempId)
        allElements.set(realId, element)
      }
    }

    // Funkcja clearAllFenceElements
    ;(window as any).clearAllFenceElements = () => {
      if (sceneRefsRef.current.scene) {
        elementManager.clearAll(sceneRefsRef.current.scene)
      }
    }

    // Funkcja removeFenceElement
    ;(window as any).removeFenceElement = (elementId: string) => {
      if (sceneRefsRef.current.scene) {
        elementManager.removeFenceElement(elementId, sceneRefsRef.current.scene)
      }
    }

    // 🎯 Funkcja do migracji istniejących elementów - dodanie baseWidth
    ;(window as any).migrateExistingElements = () => {
      console.log('🔄 Migracja istniejących elementów - dodawanie baseWidth...')
      const allElements = elementManager.getAllElements()
      
      allElements.forEach((element, elementId) => {
        if (element.name === 'section_group' && !element.userData.baseWidth) {
          const width = element.userData?.width || 0.396
          const widthMultiplier = element.userData?.widthMultiplier || 1
          const baseWidth = width / widthMultiplier
          
          element.userData.baseWidth = baseWidth
          DEBUG_LOGS && console.log(`✅ Migracja ${elementId}: baseWidth=${baseWidth.toFixed(3)}m (width=${width.toFixed(3)}m, widthMultiplier=${widthMultiplier})`)
        }
      })
      
      console.log('✅ Migracja zakończona')
    }

    // 🎯 Funkcja updateSectionWidth - odświeża przęsło z nową szerokością
    ;(window as any).updateSectionWidth = async (sectionId: string, newWidthMultiplier: number) => {
      DEBUG_LOGS && console.log(`🎯 3D updateSectionWidth: ${sectionId} → widthMultiplier=${newWidthMultiplier}`)
      
      // 🎯 Migruj istniejące elementy (dodaj baseWidth jeśli brakuje)
      ;(window as any).migrateExistingElements()
      
      if (!sceneRefsRef.current.scene || !preparedModel) {
        console.error(`❌ Scena lub model nie są gotowe`)
        return
      }
      
      // Pobierz stare dane przęsła
      const oldElement = elementManager.getAllElements().get(sectionId)
      if (!oldElement) {
        console.error(`❌ Nie znaleziono elementu ${sectionId}`)
        return
      }
      
      const oldRotation = oldElement.rotation.y
      const oldWidth = oldElement.userData?.width || 0.396
      const oldWidthMultiplier = oldElement.userData?.widthMultiplier || 1
      // 🚪 Użyj originalElementType jeśli jest dostępne (dla royal_s50 i royal_p50 gate/door elementType jest zmieniany na 'section')
      const oldElementType = oldElement.userData?.originalElementType || oldElement.userData?.elementType || 'section'
      // 🎯 Zachowaj variant i originalFenceConfigId dla Royal Mix / Muro Mix
      const oldVariant = oldElement.userData?.variant
      const oldOriginalFenceConfigId = oldElement.userData?.originalFenceConfigId
      
      // 🎯 Oblicz lewą krawędź (która musi pozostać w tym samym miejscu!)
      // ⚠️ WAŻNE: Użyj originalPosition z userData (zapisanego w addFenceElement) zamiast oldElement.position
      // oldElement.position może być modyfikowane podczas aktualizacji szerokości
      const originalPosition = oldElement.userData?.originalPosition || oldElement.position
      const oldCenterX = originalPosition.x
      const oldCenterZ = originalPosition.z
      
      // Lewo-prawo w kierunku rotacji
      const dirX = Math.cos(oldRotation)
      const dirZ = -Math.sin(oldRotation)
      
      // Lewa krawędź (cofnij się o połowę starej szerokości)
      const leftEdgeX = oldCenterX - (dirX * oldWidth / 2)
      const leftEdgeZ = oldCenterZ - (dirZ * oldWidth / 2)
      
      DEBUG_LOGS && console.log(`🎯 Stare przęsło: centrum=(${oldCenterX.toFixed(3)}, ${oldCenterZ.toFixed(3)}), width=${oldWidth.toFixed(3)}m, widthMultiplier=${oldWidthMultiplier}`)
      DEBUG_LOGS && console.log(`🎯 Lewa krawędź: (${leftEdgeX.toFixed(3)}, ${leftEdgeZ.toFixed(3)})`)
      
      // 🎯 Oblicz nową szerokość
      // Użyj zapisanego baseWidth jeśli istnieje, w przeciwnym razie oblicz
      const baseWidth = oldElement.userData?.baseWidth || (oldWidth / oldWidthMultiplier)
      const newWidth = baseWidth * newWidthMultiplier
      
      DEBUG_LOGS && console.log(`🎯 baseWidth: ${baseWidth.toFixed(3)}m (z userData: ${!!oldElement.userData?.baseWidth})`)
      
      // 🎯 Nowe centrum = lewa krawędź + połowa nowej szerokości
      const newCenterX = leftEdgeX + (dirX * newWidth / 2)
      const newCenterZ = leftEdgeZ + (dirZ * newWidth / 2)
      
      const newPosition = {
        x: newCenterX,
        y: originalPosition.y,
        z: newCenterZ
      }
      
      DEBUG_LOGS && console.log(`🎯 Nowe przęsło: centrum=(${newCenterX.toFixed(3)}, ${newCenterZ.toFixed(3)}), width=${newWidth.toFixed(3)}m, widthMultiplier=${newWidthMultiplier}`)
      
      DEBUG_LOGS && console.log(`🔄 Usuwam stare przęsło ${sectionId}`)
      elementManager.removeFenceElement(sectionId, sceneRefsRef.current.scene)
      
      DEBUG_LOGS && console.log(`➕ Dodaję nowe ${oldElementType} z widthMultiplier=${newWidthMultiplier}`)
      // 🎯 DEBUG: Loguj dane starego elementu
      console.log(`🔍 DEBUG updateSectionWidth: oldElementType=${oldElementType}, oldVariant=${oldVariant}, oldOriginalFenceConfigId=${oldOriginalFenceConfigId}`)
      console.log(`🔍 DEBUG updateSectionWidth: oldElement.userData:`, {
        originalElementType: oldElement.userData?.originalElementType,
        elementType: oldElement.userData?.elementType,
        variant: oldElement.userData?.variant,
        originalFenceConfigId: oldElement.userData?.originalFenceConfigId,
        fenceConfig: oldElement.userData?.fenceConfig?.id
      })
      
      // 🎯 Użyj właściwej konfiguracji - zachowaj originalFenceConfigId i variant!
      let currentFenceData = selectedFence || {}
      
      // 🎯 TYLKO DLA ROYAL MIX (S50/P50): Załaduj konfigurację wariantu zamiast royal-mix!
      // 🎯 WAŻNE: Sprawdź również czy oldElement.userData?.fenceConfig?.id jest royal_s50/royal_p50
      const oldFenceConfigId = oldElement.userData?.fenceConfig?.id
      // 🎯 Dla gate/door w Royal Mix: jeśli oldFenceConfigId jest royal_s50/royal_p50, użyj go bezpośrednio
      // lub jeśli oldVariant jest dostępne, załaduj konfigurację wariantu
      if (oldFenceConfigId === 'royal_s50' || oldFenceConfigId === 'royal_p50') {
        // Użyj bezpośrednio zapisanego fenceConfig
        console.log(`🎯 Royal Mix: Używam zapisanego fenceConfig: ${oldFenceConfigId}`)
        const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
        currentFenceData = await getFenceConfigWithColor(oldFenceConfigId, selectedColor || undefined) || currentFenceData
        console.log(`   Załadowano konfigurację wariantu:`, currentFenceData.id)
      } else if (oldVariant && oldOriginalFenceConfigId === 'royal-mix' && (oldVariant === 's50' || oldVariant === 'p50')) {
        console.log(`🎯 Royal Mix: Element ma variant=${oldVariant}, oldOriginalFenceConfigId=${oldOriginalFenceConfigId}`)
        console.log(`   Ładuję konfigurację royal_${oldVariant} zamiast używać royal-mix`)
        
        const variantConfigId = oldVariant === 's50' ? 'royal_s50' : 'royal_p50'
        console.log(`   variantConfigId: ${variantConfigId}`)
        
        // Załaduj konfigurację wariantu
        const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
        currentFenceData = await getFenceConfigWithColor(variantConfigId, selectedColor || undefined) || currentFenceData
        console.log(`   Załadowano konfigurację wariantu:`, currentFenceData.id)
      } else {
        console.log(`⚠️ DEBUG updateSectionWidth: NIE załadowano konfiguracji wariantu - oldVariant=${oldVariant}, oldOriginalFenceConfigId=${oldOriginalFenceConfigId}, oldFenceConfigId=${oldFenceConfigId}`)
      }
      
      await (window as any).addFenceElement(
        sectionId,
        oldElementType as any, // 🚪 Użyj prawidłowego typu (section/gate/door)
        newPosition, // 🎯 NOWA pozycja (przeliczona!)
        currentFenceData,
        undefined, // postHeight
        selectedColor,
        undefined, // onElementAdded
        newWidthMultiplier, // 🎯 Nowa szerokość!
        oldVariant // 🎯 Przekaż variant (np. s50, p50)
      )
      
      // Przywróć rotację jeśli była
      if (oldRotation !== 0) {
        if ((window as any).syncElementRotation) {
          (window as any).syncElementRotation(sectionId, oldRotation * 180 / Math.PI)
        }
      }
      
      DEBUG_LOGS && console.log(`✅ Element ${sectionId} odświeżony z widthMultiplier=${newWidthMultiplier}`)
    }

    // 🎯 ZAMKNIĘCIE OBWODU: Funkcja do ręcznego zamknięcia obwodu - używa nowego modułu PerimeterCloser
    console.log('🔧 Definiuję closeFencePerimeter na window (używam modułu PerimeterCloser)')
    
    ;(window as any).closeFencePerimeter = async (fenceConfigParam: any, selectedColor?: string, selectedVariant?: string) => {
      if (!preparedModel) {
        console.error('❌ preparedModel nie jest dostępny')
        return { success: false, message: '❌ preparedModel nie jest dostępny' }
      }
      
      if (!sceneRefsRef.current.scene) {
        console.error('❌ Scene nie jest dostępna')
        return { success: false, message: '❌ Scene nie jest dostępna' }
      }
      
      // 🎯 Dla ogrodzeń z wariantami: załaduj config wariantu (np. lupane-muro-p22 zamiast lupane-muro-mix)
      let activeFenceConfig = fenceConfigParam
      if (selectedVariant && fenceConfigParam.variantConfigs) {
        const variantConfigId = fenceConfigParam.variantConfigs[selectedVariant]
        if (variantConfigId) {
          console.log(`🎯 Ładuję config wariantu ${selectedVariant} -> ${variantConfigId}`)
          const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor)
          if (variantConfig) {
            activeFenceConfig = variantConfig
            console.log(`✅ Załadowano config wariantu dla zamykania obwodu: ${variantConfig.name}`)
          } else {
            console.warn(`⚠️ Nie można załadować config wariantu ${variantConfigId}, używam głównego config`)
          }
        }
      }
      
      // Utwórz instancję modułu zamykania obwodu
      const perimeterCloser = new PerimeterCloser(
        sceneRefsRef.current.scene,
        preparedModel
      )
      
      // Zamknij obwód - zawsze używa sekcji (section) z odpowiednim widthMultiplier
      const result = await perimeterCloser.close(
        activeFenceConfig, // 🎯 Używaj config wariantu zamiast głównego config
        selectedColor || normalizedSelectedColor || undefined,
        selectedVariant
      )
      
      if (result.success) {
        DEBUG_LOGS && console.log(`✅ ${result.message}`)
      } else {
        console.error(`❌ ${result.message}`)
      }
      
      // Zwróć wynik, żeby można było dodać sekcję do React state
      return result
    }

    // Funkcja getNextElementPosition
    ;(window as any).getNextElementPosition = (fenceData: any, _reactElements?: any[], nextElementType?: 'post' | 'section' | 'gate' | 'door', selectedVariant?: string) => {
      const allElements = Array.from(elementManager.getAllElements().values())
      if (allElements.length === 0) {
        // 🎯 Jeśli użytkownik wybrał punkt startu na planszy – użyj go jako pozycji pierwszego elementu
        const startPoint = (window as any).__fenceStartPoint as Position3D | undefined
        // 🎯 Dla Royal Mix P50: punkt startowy powinien mieć z: 0 zamiast z: -0.198
        const isRoyalP50 = fenceData?.id === 'royal-mix' && selectedVariant === 'p50'
        const defaultPosition = isRoyalP50 ? { x: -0.198, y: 0, z: 0 } : { x: -0.198, y: 0, z: -0.198 }
        const position = startPoint || defaultPosition
        console.log('📍 getNextElementPosition: Pierwszy element, używam punktu startu:', position, isRoyalP50 ? '(Royal P50 - z: 0)' : '')
        return { position, rotation: 0 }
      }

      // 🎯 FIX: Sortuj według timestamp z ID (kolejność dodawania)
      const sortedElements = allElements.sort((a: any, b: any) => {
          const aId = a.userData?.fenceElementId || ''
          const bId = b.userData?.fenceElementId || ''
          
          // Wyciągnij timestamp z ID (format: "type-timestamp")
          const aTimestamp = parseInt(aId.split('-')[1]) || 0
          const bTimestamp = parseInt(bId.split('-')[1]) || 0
          
          return aTimestamp - bTimestamp // Sortuj według kolejności dodawania
        })
        const rightmostElement = sortedElements[sortedElements.length - 1]
        
        DEBUG_LOGS && console.log(`🔍 getNextElementPosition: Posortowane elementy według timestamp:`, sortedElements.map(el => ({
          id: el.userData?.fenceElementId,
          timestamp: parseInt(el.userData?.fenceElementId?.split('-')[1]) || 0,
          x: el.position?.x?.toFixed(3)
        })))
        
      const elementId = rightmostElement.userData?.fenceElementId
      
      DEBUG_LOGS && console.log(`🔍 getNextElementPosition: rightmostElement ID=${elementId}`)
      DEBUG_LOGS && console.log(`🔍 getNextElementPosition: rightmostElement.userData:`, rightmostElement.userData)
      
      // 🎯 OBLICZ SZEROKOŚĆ NOWEGO ELEMENTU (nie poprzedniego!)
      let newElementWidth = 1.0
      
      if (nextElementType === 'gate' || nextElementType === 'door') {
        // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long, slim3d-kopia, slim-3d, slim, royal_s50, royal_p50 i wszystkich muro gate/door: używaj konfiguracji section
        const isVisionBetLong = fenceData?.id === 'vision-bet-long'
        const isVisionBetMax = fenceData?.id === 'vision-bet-max'
        const isVisionBetMedium = fenceData?.id === 'vision-bet-medium'
        const isGladkieMedium = fenceData?.id === 'gladkie_medium'
        const isGladkieMax = fenceData?.id === 'gladkie_max'
        const isGladkieLong = fenceData?.id === 'gladkie_long' || fenceData?.id === 'slim3d-kopia'
        const isSlim3d = fenceData?.id === 'slim-3d'
        const isSlim = fenceData?.id === 'slim'
        const isRoyalS50 = fenceData?.id === 'royal_s50'
        const isRoyalP50 = fenceData?.id === 'royal_p50'
        const isMuroP22 = fenceData?.id === 'lupane-muro-p22'
        const isMuroS22 = fenceData?.id === 'lupane-muro-s22'
        const isMuroP38 = fenceData?.id === 'lupane-muro-p38'
        const isMuroS38 = fenceData?.id === 'lupane-muro-s38'
        const isVisionBet =
          isVisionBetLong ||
          isVisionBetMax ||
          isVisionBetMedium ||
          isGladkieMax ||
          isGladkieLong ||
          isSlim3d ||
          isSlim ||
          isRoyalS50 ||
          isRoyalP50 ||
          isMuroP22 ||
          isMuroS22 ||
          isMuroP38 ||
          isMuroS38
        if (isVisionBet && (nextElementType === 'gate' || nextElementType === 'door')) {
          // Gate/door w vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long, slim3d-kopia, slim-3d, slim, royal_s50, royal_p50 i wszystkich muro używa konfiguracji section
          const sectionConfig = fenceData?.elements?.section
          const sectionSpacing = sectionConfig?.spacing || 0.198
          
          // Gate ma widthMultiplier = 2, door = 1 (ale dla Royal S50/P50 door też ma domyślnie 2x)
          let widthMultiplier = nextElementType === 'gate' ? 2 : 1
          if (nextElementType === 'door' && (isRoyalS50 || isRoyalP50)) {
            widthMultiplier = 2 // Dla Royal S50/P50 drzwi domyślnie 2x
          }
          
          // 🎯 Dla section: baseWidth = dimensions.width z calculateDimensions
          // calculateDimensions dla section zwraca width = defaultWidth (jeśli istnieje) lub spacing * 2
          // więc tutaj odtwarzamy tę samą logikę:
          const baseWidth =
            (sectionConfig?.defaultWidth as number | undefined) || sectionSpacing * 2
          newElementWidth = baseWidth * widthMultiplier
          
          let fenceType = ''
          if (isVisionBetLong) fenceType = 'vision-bet-long'
          else if (isVisionBetMax) fenceType = 'vision-bet-max'
          else if (isVisionBetMedium) fenceType = 'vision-bet-medium'
          else if (isGladkieMedium) fenceType = 'gladkie_medium'
          else if (isGladkieMax) fenceType = 'gladkie_max'
          else if (isGladkieLong) fenceType = fenceData?.id === 'slim3d-kopia' ? 'slim3d-kopia' : 'gladkie_long'
          else if (isSlim3d) fenceType = 'slim-3d'
          else if (isSlim) fenceType = 'slim'
          else if (isRoyalS50) fenceType = 'royal_s50'
          else if (isRoyalP50) fenceType = 'royal_p50'
          else if (isMuroP22) fenceType = 'muro_p22'
          else if (isMuroS22) fenceType = 'muro_s22'
          else if (isMuroP38) fenceType = 'muro_p38'
          else if (isMuroS38) fenceType = 'muro_s38'
          DEBUG_LOGS && console.log(`🚪 getNextElementPosition: ${fenceType} ${nextElementType}, widthMultiplier=${widthMultiplier}, baseWidth=${baseWidth.toFixed(3)}m (spacing=${sectionSpacing.toFixed(3)}m * 2), newElementWidth=${newElementWidth.toFixed(3)}m`)
        } else {
          // Dla innych ogrodzeń: użyj konfiguracji gate/door
          const gateDoorConfig = fenceData?.elements?.[nextElementType]
          const defaultWidth = gateDoorConfig?.defaultWidth || 0.3
          const widthMultiplier = nextElementType === 'gate' ? 2 : 1
          newElementWidth = defaultWidth * widthMultiplier
          
          DEBUG_LOGS && console.log(`🚪 getNextElementPosition: ${nextElementType} (nie vision-bet-long/max/medium/gladkie-max/gladkie-long/slim-3d), widthMultiplier=${widthMultiplier}, defaultWidth=${defaultWidth.toFixed(3)}m, newElementWidth=${newElementWidth.toFixed(3)}m`)
        }
      } else if (nextElementType === 'section') {
        // Dla section: użyj konfiguracji section
        const sectionConfig = fenceData?.elements?.section
        const sectionSpacing = sectionConfig?.spacing || 0.198
        const widthMultiplier = 1 // Domyślnie section ma widthMultiplier = 1
        // 🎯 Dla section: baseWidth = spacing * 2 (jak w calculateDimensions)
        const baseWidth = sectionSpacing * 2
        newElementWidth = baseWidth * widthMultiplier
        
        DEBUG_LOGS && console.log(`🎯 getNextElementPosition: section, widthMultiplier=${widthMultiplier}, baseWidth=${baseWidth.toFixed(3)}m (spacing=${sectionSpacing.toFixed(3)}m * 2), newElementWidth=${newElementWidth.toFixed(3)}m`)
      } else if (nextElementType === 'post') {
        // Dla post: w Muro Mix użyj szerokości z konfiguracji wybranego wariantu
        const isMuroMix = fenceData?.id === 'lupane-muro-mix'
        console.log(`🔍 DEBUG getNextElementPosition post: isMuroMix=${isMuroMix}, selectedVariant=${selectedVariant}, fenceData?.id=${fenceData?.id}`)
        if (isMuroMix && selectedVariant) {
          // W Muro Mix: użyj bezpośrednio wartości spacing na podstawie selectedVariant
          // S22: spacing = 0.092, szerokość posta = 0.184
          // S38: spacing = 0.074, szerokość posta = 0.148
          // P22: spacing = 0.074, szerokość posta = 0.148
          // P38: spacing = 0.074, szerokość posta = 0.148
          let postSpacing = 0.074 // Domyślnie S38/P22/P38
          if (selectedVariant === 's22') {
            postSpacing = 0.092
          } else if (selectedVariant === 's38' || selectedVariant === 'p22' || selectedVariant === 'p38') {
            postSpacing = 0.074
          }
          newElementWidth = postSpacing * 2 // spacing * 2 = szerokość posta
          console.log(`🎯 getNextElementPosition: post w Muro Mix, wybrany wariant=${selectedVariant}, postSpacing=${postSpacing.toFixed(3)}m, newElementWidth=${newElementWidth.toFixed(3)}m`)
        } else {
          // Dla innych ogrodzeń lub gdy brak wybranego wariantu: użyj szerokości z poprzedniego elementu
          newElementWidth = rightmostElement.userData?.width || 0.184
          console.log(`🎯 getNextElementPosition: post, newElementWidth=${newElementWidth.toFixed(3)}m (z poprzedniego elementu), isMuroMix=${isMuroMix}, selectedVariant=${selectedVariant}`)
        }
      } else {
        // Fallback: użyj szerokości poprzedniego elementu
        newElementWidth = rightmostElement.userData?.width || 1.0
        DEBUG_LOGS && console.log(`⚠️ getNextElementPosition: nieznany typ ${nextElementType}, używam szerokości poprzedniego elementu: ${newElementWidth.toFixed(3)}m`)
      }
      
      DEBUG_LOGS && console.log(`🔍 getNextElementPosition: newElementWidth=${newElementWidth.toFixed(3)}m dla typu ${nextElementType}`)
      
      const result = rotationManager.calculateNextElementPosition(
        rightmostElement,
        newElementWidth, // 🎯 Użyj szerokości NOWEGO elementu, nie poprzedniego!
        elementManager.getAllElements(),
        nextElementType, // 🚪 Przekaż typ nowego elementu (gate/door) dla kompensacji
        selectedVariant // 🎯 Przekaż wybrany wariant dla rozróżnienia P22 od S38
      )

      return { position: result, rotation: result.rotation }
    }


    // 🔍 DEBUG: Funkcja do wyświetlenia stanu wszystkich elementów
    ;(window as any).debugElements = () => {
      console.log('\n🔍 === DEBUG: STAN WSZYSTKICH ELEMENTÓW ===')
      const allElements = Array.from(elementManager.getAllElements().values())
    const sortedElements = allElements.sort((a: any, b: any) => {
      const aId = a.userData?.fenceElementId || ''
      const bId = b.userData?.fenceElementId || ''
      return aId.localeCompare(bId)
    })
    
      sortedElements.forEach((el: any, index: number) => {
        const id = el.userData?.fenceElementId
        const pos = el.position
        const rot = (el.rotation.y * 180) / Math.PI
        const rotRad = el.rotation.y
        const width = el.userData?.width || 0
        
        // Oblicz lewą i prawą krawędź z uwzględnieniem rotacji
        const leftEdgeDirection = new THREE.Vector3(-1, 0, 0)
        leftEdgeDirection.applyEuler(new THREE.Euler(0, rotRad, 0))
        const actualLeftEdge = pos.clone().add(leftEdgeDirection.multiplyScalar(width / 2))
        
        const dirX = Math.cos(rotRad)
        const dirZ = -Math.sin(rotRad)
        const actualRightEdge = new THREE.Vector3(
          actualLeftEdge.x + (dirX * width),
          actualLeftEdge.y,
          actualLeftEdge.z + (dirZ * width)
        )
        
        DEBUG_LOGS && console.log(`\n${index + 1}. ${id}:`)
        DEBUG_LOGS && console.log(`   Pozycja (środek): X=${pos.x.toFixed(3)}, Z=${pos.z.toFixed(3)}`)
        DEBUG_LOGS && console.log(`   Rotacja: ${rot.toFixed(1)}°`)
        DEBUG_LOGS && console.log(`   Width: ${width.toFixed(3)}m`)
        DEBUG_LOGS && console.log(`   Obliczona lewa krawędź: X=${actualLeftEdge.x.toFixed(3)}, Z=${actualLeftEdge.z.toFixed(3)}`)
        DEBUG_LOGS && console.log(`   Obliczona prawa krawędź: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)}`)
        
        const rightEdge = el.userData?.rightEdge
        if (rightEdge) {
          DEBUG_LOGS && console.log(`   rightEdge (zapisany): X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)}`)
          const diffRight = Math.sqrt(Math.pow(rightEdge.x - actualRightEdge.x, 2) + Math.pow(rightEdge.z - actualRightEdge.z, 2))
          if (diffRight > 0.001) {
            DEBUG_LOGS && console.log(`   ⚠️ BŁĄD: rightEdge różni się od obliczonej prawej krawędzi o ${diffRight.toFixed(3)}m!`)
    } else {
            DEBUG_LOGS && console.log(`   ✅ rightEdge OK`)
          }
      } else {
          DEBUG_LOGS && console.log(`   ❌ rightEdge: BRAK!`)
        }
      })
      console.log('\n🔍 === DEBUG END ===\n')
      
      console.log('💡 rightEdge (prawa krawędź) każdego elementu = punkt startu dla następnego elementu')
    }

    // 🔍 Funkcja do wyświetlenia początku i końca ogrodzenia oraz sekcji zamykającej
    ;(window as any).showFenceBounds = () => {
      console.log('\n📏 === POCZĄTEK I KONIEC OGRODZENIA ===')
      const allElements = Array.from(elementManager.getAllElements().values())
      
      if (allElements.length === 0) {
        console.log('❌ Brak elementów w ogrodzeniu')
        return
      }

      // Sortuj elementy według timestamp (kolejność dodawania)
      const sortedElements = allElements.sort((a: any, b: any) => {
        const aId = a.userData?.fenceElementId || ''
        const bId = b.userData?.fenceElementId || ''
        const aTimestamp = parseInt(aId.split('-')[1]) || 0
        const bTimestamp = parseInt(bId.split('-')[1]) || 0
        return aTimestamp - bTimestamp
      })

      const firstElement = sortedElements[0]
      const lastElement = sortedElements[sortedElements.length - 1]

      // Znajdź sekcję zamykającą
      const closingSection = allElements.find((el: any) => el.userData?.isClosingSection === true)

      // Początek ogrodzenia (leftEdge pierwszego elementu)
      let fenceStart: THREE.Vector3
      if (firstElement.userData?.leftEdge instanceof THREE.Vector3) {
        fenceStart = firstElement.userData.leftEdge.clone()
      } else {
        // Oblicz leftEdge na podstawie pozycji, rotacji i szerokości
        const rot = firstElement.rotation.y
        const w = firstElement.userData?.width || 0.396
        const dx = Math.cos(rot)
        const dz = -Math.sin(rot)
        fenceStart = new THREE.Vector3(
          firstElement.position.x - (w / 2) * dx,
          firstElement.position.y,
          firstElement.position.z - (w / 2) * dz
        )
      }

      // Koniec ogrodzenia (rightEdge ostatniego elementu)
      let fenceEnd: THREE.Vector3
      if (lastElement.userData?.rightEdge instanceof THREE.Vector3) {
        fenceEnd = lastElement.userData.rightEdge.clone()
      } else {
        // Oblicz rightEdge na podstawie pozycji, rotacji i szerokości
        const rot = lastElement.rotation.y
        const w = lastElement.userData?.width || 0.396
        const dx = Math.cos(rot)
        const dz = -Math.sin(rot)
        fenceEnd = new THREE.Vector3(
          lastElement.position.x + (w / 2) * dx,
          lastElement.position.y,
          lastElement.position.z + (w / 2) * dz
        )
      }

      console.log('\n📍 POCZĄTEK OGRODZENIA:')
      console.log(`   X: ${fenceStart.x.toFixed(6)}`)
      console.log(`   Y: ${fenceStart.y.toFixed(6)}`)
      console.log(`   Z: ${fenceStart.z.toFixed(6)}`)

      console.log('\n📍 KONIEC OGRODZENIA:')
      console.log(`   X: ${fenceEnd.x.toFixed(6)}`)
      console.log(`   Y: ${fenceEnd.y.toFixed(6)}`)
      console.log(`   Z: ${fenceEnd.z.toFixed(6)}`)

      if (closingSection) {
        // Początek sekcji zamykającej (leftEdge)
        let closingStart: THREE.Vector3
        if (closingSection.userData?.leftEdge instanceof THREE.Vector3) {
          closingStart = closingSection.userData.leftEdge.clone()
        } else {
          // Oblicz leftEdge na podstawie pozycji, rotacji i szerokości
          const rot = closingSection.rotation.y
          const w = closingSection.userData?.width || 0.396
          const dx = Math.cos(rot)
          const dz = -Math.sin(rot)
          closingStart = new THREE.Vector3(
            closingSection.position.x - (w / 2) * dx,
            closingSection.position.y,
            closingSection.position.z - (w / 2) * dz
          )
        }

        // Koniec sekcji zamykającej (rightEdge)
        let closingEnd: THREE.Vector3
        if (closingSection.userData?.rightEdge instanceof THREE.Vector3) {
          closingEnd = closingSection.userData.rightEdge.clone()
        } else {
          // Oblicz rightEdge na podstawie pozycji, rotacji i szerokości
          const rot = closingSection.rotation.y
          const w = closingSection.userData?.width || 0.396
          const dx = Math.cos(rot)
          const dz = -Math.sin(rot)
          closingEnd = new THREE.Vector3(
            closingSection.position.x + (w / 2) * dx,
            closingSection.position.y,
            closingSection.position.z + (w / 2) * dz
          )
        }

        console.log('\n📍 POCZĄTEK SEKCJI ZAMYKAJĄCEJ:')
        console.log(`   X: ${closingStart.x.toFixed(6)}`)
        console.log(`   Y: ${closingStart.y.toFixed(6)}`)
        console.log(`   Z: ${closingStart.z.toFixed(6)}`)

        console.log('\n📍 KONIEC SEKCJI ZAMYKAJĄCEJ:')
        console.log(`   X: ${closingEnd.x.toFixed(6)}`)
        console.log(`   Y: ${closingEnd.y.toFixed(6)}`)
        console.log(`   Z: ${closingEnd.z.toFixed(6)}`)
      } else {
        console.log('\n⚠️ Brak sekcji zamykającej')
      }

      // Wyświetl krawędzie wszystkich elementów
      console.log('\n📐 === KRAWĘDZIE ELEMENTÓW ===')
      sortedElements.forEach((el: any, index: number) => {
        const id = el.userData?.fenceElementId || `element-${index}`
        const elementType = el.userData?.elementType || 'unknown'
        
        // Oblicz leftEdge
        let leftEdge: THREE.Vector3
        if (el.userData?.leftEdge instanceof THREE.Vector3) {
          leftEdge = el.userData.leftEdge.clone()
        } else {
          const rot = el.rotation.y
          const w = el.userData?.width || 0.396
          const dx = Math.cos(rot)
          const dz = -Math.sin(rot)
          leftEdge = new THREE.Vector3(
            el.position.x - (w / 2) * dx,
            el.position.y,
            el.position.z - (w / 2) * dz
          )
        }
        
        // Oblicz rightEdge
        let rightEdge: THREE.Vector3
        if (el.userData?.rightEdge instanceof THREE.Vector3) {
          rightEdge = el.userData.rightEdge.clone()
        } else {
          const rot = el.rotation.y
          const w = el.userData?.width || 0.396
          const dx = Math.cos(rot)
          const dz = -Math.sin(rot)
          rightEdge = new THREE.Vector3(
            el.position.x + (w / 2) * dx,
            el.position.y,
            el.position.z + (w / 2) * dz
          )
        }
        
        console.log(`\n${index + 1}. ${id} (${elementType}):`)
        console.log(`   LEWA KRAWĘDŹ:`)
        console.log(`      X: ${leftEdge.x.toFixed(6)}`)
        console.log(`      Y: ${leftEdge.y.toFixed(6)}`)
        console.log(`      Z: ${leftEdge.z.toFixed(6)}`)
        console.log(`   PRAWA KRAWĘDŹ:`)
        console.log(`      X: ${rightEdge.x.toFixed(6)}`)
        console.log(`      Y: ${rightEdge.y.toFixed(6)}`)
        console.log(`      Z: ${rightEdge.z.toFixed(6)}`)
      })

      console.log('\n==========================================\n')
    }

    // Funkcja syncElementRotation - ustawia odziedziczoną rotację BEZ przesuwania elementu
    ;(window as any).syncElementRotation = (elementId: string, rotation: number) => {
      const elementGroup = elementManager.getElement(elementId)
      if (!elementGroup) return

      // 🔑 BLOKADA: Nie aktualizuj pozycji elementu zamykającego!
      if (elementGroup.userData?.isClosingSection) {
        DEBUG_LOGS && console.log(`🚫 syncElementRotation: Element zamykający ${elementId} - pomijam aktualizację pozycji!`)
        // Tylko zaktualizuj rotację wizualną i userData, ale NIE przesuwaj pozycji
        const rotationInRadians = (rotation * Math.PI) / 180
        elementGroup.rotation.y = rotationInRadians
        elementGroup.userData.inheritedRotation = rotation
        elementGroup.userData.absoluteRotation = rotation
        elementGroup.userData.reactRotation = 0
        return
      }

      DEBUG_LOGS && console.log(`🔄 syncElementRotation: ${elementId}, rotation=${rotation}°`)
      DEBUG_LOGS && console.log(`🔍 Element name: ${elementGroup.name}, elementType: ${elementGroup.userData?.elementType}`)
      
      // 🔍 DEBUG: Sprawdź wszystkie dane elementu
      const fenceConfig = elementGroup.userData?.fenceConfig
      DEBUG_LOGS && console.log(`🔍 DEBUG: fenceConfig:`, fenceConfig)
      DEBUG_LOGS && console.log(`🔍 DEBUG: fenceConfig?.id:`, fenceConfig?.id)
      DEBUG_LOGS && console.log(`🔍 DEBUG: elementGroup.name:`, elementGroup.name)
      DEBUG_LOGS && console.log(`🔍 DEBUG: userData:`, elementGroup.userData)
      
      const isSlim3D = fenceConfig?.id === 'slim-3d'
      const isSlim3DSection = elementGroup.name === 'section_group' && isSlim3D
      const isSlim3DPost = elementGroup.name === 'post_group' && isSlim3D
      
      DEBUG_LOGS && console.log(`🔍 DEBUG: isSlim3D:`, isSlim3D)
      DEBUG_LOGS && console.log(`🔍 DEBUG: isSlim3DSection:`, isSlim3DSection)
      DEBUG_LOGS && console.log(`🔍 DEBUG: isSlim3DPost:`, isSlim3DPost)
      
      // 🎯 Dla Slim 3D (section i post): oblicz pivot point przed rotacją i aktualizuj pozycję po rotacji
      let pivotPointBeforeRotation: THREE.Vector3 | null = null
      let oldRotationRad = 0
      if (isSlim3DSection || isSlim3DPost) {
        // Zapisz starą rotację i oblicz pivot point PRZED rotacją
        const oldRotation = elementGroup.userData?.absoluteRotation || elementGroup.rotation.y * 180 / Math.PI
        oldRotationRad = (oldRotation * Math.PI) / 180
        const pos = elementGroup.position.clone()
        const width = elementGroup.userData?.width || 0.4
        const geometryOffsetZ = elementGroup.userData?.geometryOffsetZ || 0
        
        DEBUG_LOGS && console.log(`  🔍 DEBUG Slim 3D ${isSlim3DSection ? 'section' : 'post'} przed rotacją:`)
        DEBUG_LOGS && console.log(`    - oldRotation: ${oldRotation.toFixed(2)}°`)
        DEBUG_LOGS && console.log(`    - position: X=${pos.x.toFixed(6)}, Z=${pos.z.toFixed(6)}`)
        DEBUG_LOGS && console.log(`    - width: ${width.toFixed(6)}`)
        DEBUG_LOGS && console.log(`    - geometryOffsetZ: ${geometryOffsetZ.toFixed(6)}`)
        
        if (isSlim3DSection) {
          // Dla section: użyj baseWidth i extraOffset (jak wcześniej)
          const baseWidth = elementGroup.userData?.baseWidth || width
          const widthMultiplier = elementGroup.userData?.widthMultiplier || 1
          
          DEBUG_LOGS && console.log(`    - baseWidth: ${baseWidth.toFixed(6)}`)
          DEBUG_LOGS && console.log(`    - widthMultiplier: ${widthMultiplier}`)
          
          // Oblicz leftEdgeOffset (jak w calculatePivotPoint)
          let leftEdgeOffset = new THREE.Vector3(-baseWidth / 2, 0, geometryOffsetZ)
          
          // Dodaj extraOffset dla Slim 3D
          const extraOffset = widthMultiplier === 1 ? 0 : (widthMultiplier === 2 ? -0.396 : widthMultiplier === 3 ? -0.594 : widthMultiplier === 4 ? -0.792 : widthMultiplier === 5 ? -0.990 : 0)
          leftEdgeOffset.x += extraOffset
          
          DEBUG_LOGS && console.log(`    - leftEdgeOffset przed rotacją: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
          DEBUG_LOGS && console.log(`    - extraOffset: ${extraOffset.toFixed(6)}`)
          
          // Obróć offset o starą rotację
          leftEdgeOffset.applyEuler(new THREE.Euler(0, oldRotationRad, 0))
          
          DEBUG_LOGS && console.log(`    - leftEdgeOffset po rotacji: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
          
          // Dodaj rightOffset jeśli istnieje
          const rightOffset = elementGroup.userData?.rightOffset || 0
          if (rightOffset !== 0) {
            const rightOffsetVector = new THREE.Vector3(rightOffset, 0, 0)
            rightOffsetVector.applyEuler(new THREE.Euler(0, oldRotationRad, 0))
            leftEdgeOffset.add(rightOffsetVector)
            DEBUG_LOGS && console.log(`    - rightOffset: ${rightOffset.toFixed(6)}, vector: X=${rightOffsetVector.x.toFixed(6)}, Z=${rightOffsetVector.z.toFixed(6)}`)
          }
          
          pivotPointBeforeRotation = pos.clone().add(leftEdgeOffset)
          DEBUG_LOGS && console.log(`  🎯 Slim 3D section: pivot point przed rotacją: X=${pivotPointBeforeRotation.x.toFixed(6)}, Z=${pivotPointBeforeRotation.z.toFixed(6)}`)
        } else if (isSlim3DPost) {
          // Dla post: pivot point w lewej krawędzi (jak dla sections, ale bez extraOffset)
          let leftEdgeOffset = new THREE.Vector3(-width / 2, 0, geometryOffsetZ)
          
          DEBUG_LOGS && console.log(`    - leftEdgeOffset przed rotacją: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
          
          // Obróć offset o starą rotację
          leftEdgeOffset.applyEuler(new THREE.Euler(0, oldRotationRad, 0))
          
          DEBUG_LOGS && console.log(`    - leftEdgeOffset po rotacji: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
          
          // Dodaj rightOffset jeśli istnieje
          const rightOffset = elementGroup.userData?.rightOffset || 0
          if (rightOffset !== 0) {
            const rightOffsetVector = new THREE.Vector3(rightOffset, 0, 0)
            rightOffsetVector.applyEuler(new THREE.Euler(0, oldRotationRad, 0))
            leftEdgeOffset.add(rightOffsetVector)
            DEBUG_LOGS && console.log(`    - rightOffset: ${rightOffset.toFixed(6)}, vector: X=${rightOffsetVector.x.toFixed(6)}, Z=${rightOffsetVector.z.toFixed(6)}`)
          }
          
          pivotPointBeforeRotation = pos.clone().add(leftEdgeOffset)
          DEBUG_LOGS && console.log(`  🎯 Slim 3D post: pivot point przed rotacją: X=${pivotPointBeforeRotation.x.toFixed(3)}, Z=${pivotPointBeforeRotation.z.toFixed(3)}`)
        }
      }

      // 🔥 NAJPIERW ustaw rotację wizualną
      const rotationInRadians = (rotation * Math.PI) / 180
      elementGroup.rotation.y = rotationInRadians

      // 🎯 Dla Slim 3D (section i post): aktualizuj pozycję, żeby pivot point pozostał w miejscu
      if ((isSlim3DSection || isSlim3DPost) && pivotPointBeforeRotation) {
        const width = elementGroup.userData?.width || 0.4
        const geometryOffsetZ = elementGroup.userData?.geometryOffsetZ || 0
        
        DEBUG_LOGS && console.log(`  🔍 DEBUG Slim 3D ${isSlim3DSection ? 'section' : 'post'} po rotacji:`)
        DEBUG_LOGS && console.log(`    - newRotation: ${rotation.toFixed(2)}°`)
        DEBUG_LOGS && console.log(`    - current position: X=${elementGroup.position.x.toFixed(6)}, Z=${elementGroup.position.z.toFixed(6)}`)
        
        let leftEdgeOffset: THREE.Vector3
        
        if (isSlim3DSection) {
          // Dla section: użyj baseWidth i extraOffset
          const baseWidth = elementGroup.userData?.baseWidth || width
          const widthMultiplier = elementGroup.userData?.widthMultiplier || 1
          
          DEBUG_LOGS && console.log(`    - baseWidth: ${baseWidth.toFixed(6)}`)
          DEBUG_LOGS && console.log(`    - widthMultiplier: ${widthMultiplier}`)
          
          // Oblicz leftEdgeOffset dla nowej rotacji
          leftEdgeOffset = new THREE.Vector3(-baseWidth / 2, 0, geometryOffsetZ)
          
          // Dodaj extraOffset dla Slim 3D
          const extraOffset = widthMultiplier === 1 ? 0 : (widthMultiplier === 2 ? -0.396 : widthMultiplier === 3 ? -0.594 : widthMultiplier === 4 ? -0.792 : widthMultiplier === 5 ? -0.990 : 0)
          leftEdgeOffset.x += extraOffset
          
          DEBUG_LOGS && console.log(`    - leftEdgeOffset przed rotacją: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
          DEBUG_LOGS && console.log(`    - extraOffset: ${extraOffset.toFixed(6)}`)
        } else {
          // Dla post: użyj width bez extraOffset
          leftEdgeOffset = new THREE.Vector3(-width / 2, 0, geometryOffsetZ)
          DEBUG_LOGS && console.log(`    - leftEdgeOffset przed rotacją: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
        }
        
        // Obróć offset o nową rotację
        leftEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
        
        DEBUG_LOGS && console.log(`    - leftEdgeOffset po rotacji: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
        
        // Dodaj rightOffset jeśli istnieje
        const rightOffset = elementGroup.userData?.rightOffset || 0
        if (rightOffset !== 0) {
          const rightOffsetVector = new THREE.Vector3(rightOffset, 0, 0)
          rightOffsetVector.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
          leftEdgeOffset.add(rightOffsetVector)
          DEBUG_LOGS && console.log(`    - rightOffset: ${rightOffset.toFixed(6)}, vector: X=${rightOffsetVector.x.toFixed(6)}, Z=${rightOffsetVector.z.toFixed(6)}`)
        }
        
        // Oblicz nową pozycję, żeby pivot point pozostał w miejscu
        const newPosition = pivotPointBeforeRotation.clone().sub(leftEdgeOffset)
        
        DEBUG_LOGS && console.log(`    - pivotPointBeforeRotation: X=${pivotPointBeforeRotation.x.toFixed(6)}, Z=${pivotPointBeforeRotation.z.toFixed(6)}`)
        DEBUG_LOGS && console.log(`    - final leftEdgeOffset: X=${leftEdgeOffset.x.toFixed(6)}, Z=${leftEdgeOffset.z.toFixed(6)}`)
        DEBUG_LOGS && console.log(`    - newPosition: X=${newPosition.x.toFixed(6)}, Z=${newPosition.z.toFixed(6)}`)
        
        elementGroup.position.set(newPosition.x, newPosition.y, newPosition.z)
        DEBUG_LOGS && console.log(`  🎯 Slim 3D ${isSlim3DSection ? 'section' : 'post'}: zaktualizowano pozycję, żeby pivot point pozostał w miejscu`)
      }

      // 🔥 POTEM ustaw userData (po rotacji!)
      elementGroup.userData.inheritedRotation = rotation
      elementGroup.userData.rotation = 0
      elementGroup.userData.absoluteRotation = rotation
      elementGroup.userData.reactRotation = 0

      // 🔥 OBLICZ PRAWĄ KRAWĘDŹ PO ROTACJI
      const width = elementGroup.userData?.width || 0.4
      const pos = elementGroup.position

      // Kierunki PO rotacji
      const dirX = Math.cos(rotationInRadians)
      const dirZ = -Math.sin(rotationInRadians)

      let rightEdgeX, rightEdgeZ

      // 🚪 Dla door i gate w gladkie_long (i podobnych): NIE nadpisuj rightEdge, bo już zostało obliczone w elementManager.ts z uwzględnieniem rotacji
      if (elementGroup.name === 'door_group' || elementGroup.name === 'gate_group') {
        const elementType = elementGroup.userData?.elementType
        const fenceConfig = elementGroup.userData?.fenceConfig
        const isVisionBetLongGateDoor = fenceConfig?.id === 'vision-bet-long' && (elementType === 'gate' || elementType === 'door')
        const isVisionBetMaxGateDoor = fenceConfig?.id === 'vision-bet-max' && (elementType === 'gate' || elementType === 'door')
        const isVisionBetMediumGateDoor = fenceConfig?.id === 'vision-bet-medium' && (elementType === 'gate' || elementType === 'door')
        const isGladkieMediumGateDoor = fenceConfig?.id === 'gladkie_medium' && (elementType === 'gate' || elementType === 'door')
        const isGladkieMaxGateDoor = fenceConfig?.id === 'gladkie_max' && (elementType === 'gate' || elementType === 'door')
        const isGladkieLongGateDoor = fenceConfig?.id === 'gladkie_long' && (elementType === 'gate' || elementType === 'door')
        const isSlim3dGateDoor = fenceConfig?.id === 'slim-3d' && (elementType === 'gate' || elementType === 'door')
        const isSlimGateDoor = fenceConfig?.id === 'slim' && (elementType === 'gate' || elementType === 'door')
        const isVisionBetGateDoor = isVisionBetLongGateDoor || isVisionBetMaxGateDoor || isVisionBetMediumGateDoor || isGladkieMediumGateDoor || isGladkieMaxGateDoor || isGladkieLongGateDoor || isSlim3dGateDoor || isSlimGateDoor
        
        if (isVisionBetGateDoor) {
          // Dla gate/door w gladkie_long (i podobnych): rightEdge już zostało obliczone w elementManager.ts z uwzględnieniem rotacji
          // Nie nadpisuj go - użyj już obliczonego
          const existingRightEdge = elementGroup.userData?.rightEdge
          if (existingRightEdge) {
            rightEdgeX = existingRightEdge.x
            rightEdgeZ = existingRightEdge.z
            DEBUG_LOGS && console.log(`  🚪 ${elementGroup.name === 'door_group' ? 'Door' : 'Gate'}: używam rightEdge z elementManager (nie nadpisuję): X=${rightEdgeX.toFixed(3)}, Z=${rightEdgeZ.toFixed(3)}`)
          } else {
            // Fallback: oblicz normalnie
            const leftEdgeX = pos.x - (dirX * width / 2)
            const leftEdgeZ = pos.z - (dirZ * width / 2)
            rightEdgeX = leftEdgeX + (dirX * width)
            rightEdgeZ = leftEdgeZ + (dirZ * width)
            DEBUG_LOGS && console.log(`  ⚠️ ${elementGroup.name === 'door_group' ? 'Door' : 'Gate'}: brak rightEdge, użyto fallbacka`)
          }
        } else {
          // Dla gate/door (nie gladkie_long): oblicz normalnie
          const leftEdgeX = pos.x - (dirX * width / 2)
          const leftEdgeZ = pos.z - (dirZ * width / 2)
          rightEdgeX = leftEdgeX + (dirX * width)
          rightEdgeZ = leftEdgeZ + (dirZ * width)
          DEBUG_LOGS && console.log(`  🚪 ${elementGroup.name === 'door_group' ? 'Door' : 'Gate'}: obliczono rightEdge normalnie (nie gladkie_long)`)
        }
      } else {
        // 🔥 Dla innych elementów: oblicz normalnie
        // 🎯 Dla Muro P22 section: użyj rightEdge z elementManager.ts i tylko obróć go (jak dla gate/door)
        const fenceConfig = elementGroup.userData?.fenceConfig
        const isMuroP22Section = elementGroup.name === 'section_group' && fenceConfig?.id === 'lupane-muro-p22'
        const isMuroP38Section = elementGroup.name === 'section_group' && fenceConfig?.id === 'lupane-muro-p38'
        const isMuroS22Section = elementGroup.name === 'section_group' && fenceConfig?.id === 'lupane-muro-s22'
        const isMuroS38Section = elementGroup.name === 'section_group' && fenceConfig?.id === 'lupane-muro-s38'
        const isSlim3DSection = elementGroup.name === 'section_group' && fenceConfig?.id === 'slim-3d'
        
        if (isSlim3DSection) {
          // 🎯 Dla Slim 3D: użyj baseWidth zamiast width dla obliczania rightEdge (jak Vision-Bet Long)
          const baseWidth = elementGroup.userData?.baseWidth || elementGroup.userData?.width || width
          const rightEdgeOffset = new THREE.Vector3(baseWidth / 2, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
          
          rightEdgeX = pos.x + rightEdgeOffset.x
          rightEdgeZ = pos.z + rightEdgeOffset.z
          
          DEBUG_LOGS && console.log(`  🎯 Slim 3D section: używam baseWidth=${baseWidth.toFixed(3)} zamiast width=${width.toFixed(3)} dla rightEdge`)
          DEBUG_LOGS && console.log(`    - baseWidth=${baseWidth.toFixed(3)}, rotation=${rotation}°, rotationRad=${rotationInRadians.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - rightEdgeOffset: X=${rightEdgeOffset.x.toFixed(3)}, Z=${rightEdgeOffset.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - final rightEdge: X=${rightEdgeX.toFixed(3)}, Z=${rightEdgeZ.toFixed(3)}`)
        } else if (isMuroP22Section) {
          // 🎯 Dla Muro P22: użyj tej samej metody co fallback w calculateNextElementPosition
          // Prawa krawędź od środka: obróć wektor (width/2, 0, 0) o aktualną rotację
          // To jest proste obliczenie bez kompensacji - tak jak dla niezrotowanych elementów
          const rightEdgeOffset = new THREE.Vector3(width / 2, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
          
          rightEdgeX = pos.x + rightEdgeOffset.x
          rightEdgeZ = pos.z + rightEdgeOffset.z
          
          DEBUG_LOGS && console.log(`  🎯 Muro P22 section: proste obliczenie rightEdge bez kompensacji (jak fallback)`)
          DEBUG_LOGS && console.log(`    - width=${width.toFixed(3)}, rotation=${rotation}°, rotationRad=${rotationInRadians.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - rightEdgeOffset: X=${rightEdgeOffset.x.toFixed(3)}, Z=${rightEdgeOffset.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - final rightEdge: X=${rightEdgeX.toFixed(3)}, Z=${rightEdgeZ.toFixed(3)}`)
        } else if (isMuroP38Section) {
          // 🎯 Dla Muro P38: użyj tej samej metody co fallback w calculateNextElementPosition
          // Prawa krawędź od środka: obróć wektor (width/2, 0, 0) o aktualną rotację
          // To jest proste obliczenie bez kompensacji - tak jak dla niezrotowanych elementów
          const rightEdgeOffset = new THREE.Vector3(width / 2, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
          
          rightEdgeX = pos.x + rightEdgeOffset.x
          rightEdgeZ = pos.z + rightEdgeOffset.z
          
          DEBUG_LOGS && console.log(`  🎯 Muro P38 section: proste obliczenie rightEdge bez kompensacji (jak fallback)`)
          DEBUG_LOGS && console.log(`    - width=${width.toFixed(3)}, rotation=${rotation}°, rotationRad=${rotationInRadians.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - rightEdgeOffset: X=${rightEdgeOffset.x.toFixed(3)}, Z=${rightEdgeOffset.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - final rightEdge: X=${rightEdgeX.toFixed(3)}, Z=${rightEdgeZ.toFixed(3)}`)
        } else if (isMuroS22Section) {
          // 🎯 Dla Muro S22: użyj tej samej metody co fallback w calculateNextElementPosition
          // Prawa krawędź od środka: obróć wektor (width/2, 0, 0) o aktualną rotację
          // To jest proste obliczenie bez kompensacji - tak jak dla niezrotowanych elementów
          const rightEdgeOffset = new THREE.Vector3(width / 2, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
          
          rightEdgeX = pos.x + rightEdgeOffset.x
          rightEdgeZ = pos.z + rightEdgeOffset.z
          
          DEBUG_LOGS && console.log(`  🎯 Muro S22 section: proste obliczenie rightEdge bez kompensacji (jak fallback)`)
          DEBUG_LOGS && console.log(`    - width=${width.toFixed(3)}, rotation=${rotation}°, rotationRad=${rotationInRadians.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - rightEdgeOffset: X=${rightEdgeOffset.x.toFixed(3)}, Z=${rightEdgeOffset.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - final rightEdge: X=${rightEdgeX.toFixed(3)}, Z=${rightEdgeZ.toFixed(3)}`)
        } else if (isMuroS38Section) {
          // 🎯 Dla Muro S38: użyj tej samej metody co fallback w calculateNextElementPosition
          // Prawa krawędź od środka: obróć wektor (width/2, 0, 0) o aktualną rotację
          // To jest proste obliczenie bez kompensacji - tak jak dla niezrotowanych elementów
          const rightEdgeOffset = new THREE.Vector3(width / 2, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
          
          rightEdgeX = pos.x + rightEdgeOffset.x
          rightEdgeZ = pos.z + rightEdgeOffset.z
          
          DEBUG_LOGS && console.log(`  🎯 Muro S38 section: proste obliczenie rightEdge bez kompensacji (jak fallback)`)
          DEBUG_LOGS && console.log(`    - width=${width.toFixed(3)}, rotation=${rotation}°, rotationRad=${rotationInRadians.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - rightEdgeOffset: X=${rightEdgeOffset.x.toFixed(3)}, Z=${rightEdgeOffset.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`    - final rightEdge: X=${rightEdgeX.toFixed(3)}, Z=${rightEdgeZ.toFixed(3)}`)
        } else {
          // Standardowa logika dla innych ogrodzeń
          const leftEdgeX = pos.x - (dirX * width / 2)
          const leftEdgeZ = pos.z - (dirZ * width / 2)
          rightEdgeX = leftEdgeX + (dirX * width)
          rightEdgeZ = leftEdgeZ + (dirZ * width)

          // 🔥 ZASTOSUJ OFFSET z mapy (tylko dla section)
          if (elementGroup.name === 'section_group') {
            const widthMultiplier = elementGroup.userData?.widthMultiplier || 1
            const fenceId = fenceConfig?.id

            let offset = 0
            
            if (fenceId && ROTATION_RIGHT_EDGE_OFFSETS[fenceId]) {
              // Dla section: użyj offsetów z fenceId
              offset = ROTATION_RIGHT_EDGE_OFFSETS[fenceId][widthMultiplier.toString()] || 0
            }
            
            if (offset !== 0) {
              DEBUG_LOGS && console.log(`  📐 Offset dla ${elementGroup.name} ${widthMultiplier}x: ${offset.toFixed(3)}m`)
              rightEdgeX += dirX * offset
              rightEdgeZ += dirZ * offset
            }
          }
        }
      }

      const actualRightEdge = new THREE.Vector3(rightEdgeX, pos.y, rightEdgeZ)
      elementGroup.userData.rightEdge = actualRightEdge.clone()
      
      DEBUG_LOGS && console.log(`✅ rightEdge: X:${rightEdgeX.toFixed(3)}, Z:${rightEdgeZ.toFixed(3)}`)
    }

    // Funkcja removeElement - usuwa element z 3D sceny
    ;(window as any).removeElement = (elementId: string) => {
      DEBUG_LOGS && console.log(`🗑️ 3D removeElement: Usuwam element ${elementId}`)
      
      if (!sceneRefsRef.current.scene) {
        console.error(`❌ 3D removeElement: Scena nie jest gotowa`)
        return false
      }
      
      const success = elementManager.removeElement(elementId, sceneRefsRef.current.scene)
      if (success) {
        DEBUG_LOGS && console.log(`✅ 3D removeElement: Element ${elementId} usunięty z 3D sceny`)
      } else {
        console.error(`❌ 3D removeElement: Nie udało się usunąć elementu ${elementId}`)
      }
      
      return success
    }

    // Funkcja removeLastElement - usuwa ostatni element z 3D sceny
    ;(window as any).removeLastElement = () => {
      DEBUG_LOGS && console.log(`🗑️ 3D removeLastElement: Usuwam ostatni element`)
      
      if (!sceneRefsRef.current.scene) {
        console.error(`❌ 3D removeLastElement: Scena nie jest gotowa`)
        return false
      }
      
      const success = elementManager.removeLastElement(sceneRefsRef.current.scene)
      if (success) {
        DEBUG_LOGS && console.log(`✅ 3D removeLastElement: Ostatni element usunięty z 3D sceny`)
      } else {
        console.error(`❌ 3D removeLastElement: Nie udało się usunąć ostatniego elementu`)
      }
      
      return success
    }
  }, [
    preparedModel,
    elementManager,
    rotationManager,
    selectedFence,
    normalizedSelectedColor,
    selectedColor,
    onElementSelect,
  ])

  /**
   * Reset sceny przy resetCounter
   */
  useEffect(() => {
    if (resetCounter > 0 && sceneRefsRef.current.scene) {
      elementManager.clearAll(sceneRefsRef.current.scene)
    }
  }, [resetCounter, elementManager])

  /**
   * Wymuś renderowanie przy zmianie viewMode
   */
  useEffect(() => {
    if (viewMode === '3D' && containerRef.current) {
      const refs = sceneRefsRef.current
      if (refs.renderer && refs.scene && refs.camera) {
        handleResize(containerRef.current, refs)
        refs.renderer.render(refs.scene, refs.camera)
      }
    }
  }, [viewMode])
  
  /**
   * Render
   */
  return (
    <div className="relative w-full h-full" data-threejs>
      <div ref={containerRef} className="w-full h-full" />
      
      <div className="absolute top-4 right-4 flex space-x-2">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('toggleViewMode'))}
          className="px-3 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm hover:bg-white transition-colors flex items-center space-x-2"
          title={`Przełącz na ${viewMode === '3D' ? '2D' : '3D'}`}
        >
          <span className="text-lg">{viewMode === '3D' ? '📐' : '🔍'}</span>
          <span className="text-sm font-medium text-gray-700">
            {viewMode === '3D' ? '2D' : '3D'}
          </span>
        </button>
        
        <button
          onClick={() => resetCamera(sceneRefsRef.current)}
          className="p-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm hover:bg-white transition-colors"
          title="Resetuj kamerę"
        >
          🎯
        </button>
        
        <button
          onClick={() => zoomToFit(sceneRefsRef.current)}
          className="p-2 bg-blue-50/90 backdrop-blur-sm border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 transition-colors"
          title="Przybliż kamerę do elementów"
        >
          🔍
        </button>
      </div>
    </div>
  )
}

export default ThreeJSCanvas3D
